import { createFileRoute, notFound, redirect, Link, type ErrorComponentProps } from '@tanstack/react-router'
import { z } from 'zod'
import { OrderContent } from '@/features/accommodation/order/components/OrderContent'
import { orderQueryOptions, accommodationDetailQueryOptions } from '@/api/accommodation/hooks'
import { Button } from '@/components/ui/button'
import { HttpError } from '@/api/http'
import { SITE_NAME, noindexMeta } from '@/lib/seo/siteMeta'

const orderParamsSchema = z.object({
  orderNumber: z.coerce.string(),
})

const orderSearchSchema = z.object({
  stripeUrl: z.string().optional(),
})

function OrderErrorComponent({ error }: ErrorComponentProps) {
  const { orderNumber } = Route.useParams()

  let title = 'Chyba při načítání'
  let message = 'Nepodařilo se načíst objednávku. Zkuste to prosím znovu.'

  if (error instanceof HttpError) {
    if (error.status === 404) {
      title = 'Objednávka nenalezena'
      message = 'Objednávka nebyla nalezena. Zkontrolujte prosím číslo objednávky.'
    } else if (error.status === 400) {
      title = 'Neplatná objednávka'
      message = 'Neplatný formát čísla objednávky.'
    } else if (error.status >= 500) {
      title = 'Chyba serveru'
      message = 'Chyba serveru. Zkuste to prosím později.'
    }
  }

  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
      <h1 className="mb-2 text-2xl font-semibold text-slate-700">{title}</h1>
      <p className="mb-2 max-w-sm text-slate-500">{message}</p>
      <p className="mb-6 text-sm text-slate-400">Číslo objednávky: {orderNumber}</p>
      <Button asChild>
        <Link to="/">Zpět na hlavní stránku</Link>
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/_layout/objednavka/$orderNumber')({
  component: OrderPage,
  errorComponent: OrderErrorComponent,
  params: {
    parse: (params) => orderParamsSchema.parse(params),
  },
  validateSearch: orderSearchSchema,
  beforeLoad: ({ search }) => {
    if (search.stripeUrl) {
      throw redirect({ href: search.stripeUrl })
    }
  },
  loader: async ({ context, params }) => {
    try {
      // Prefetch order data
      const order = await context.queryClient.query({
        ...orderQueryOptions(params.orderNumber),
        staleTime: 'static',
      })
      // Prefetch accommodation detail (only used for check-in/out times) if the
      // order carries both a location and a unit.
      if (order.location?.slug && order.unit?.slug) {
        await context.queryClient.query({
          ...accommodationDetailQueryOptions(order.location.slug, order.unit.slug),
          staleTime: 'static',
        })
      }
    } catch (error) {
      // An unknown order must surface as a real 404, not an SSR 500.
      if (error instanceof HttpError && error.status === 404) {
        throw notFound()
      }
      throw error
    }
  },
  head: () => ({
    meta: [{ title: `Objednávka | ${SITE_NAME}` }, ...noindexMeta()],
  }),
})

function OrderPage() {
  const { orderNumber } = Route.useParams()

  return <OrderContent orderNumber={orderNumber} />
}
